import { TechemTheme } from '@techem/techem-theme';
import { Button } from '@techem/ui-components';
import arrowWithDottedLine from 'presentation/assets/icons/png/3x/arrow-dotted-line@3x.png';
import circledCheckmarkIcon from 'presentation/assets/icons/png/3x/circle-check-info@3x.png';
import invoiceIcon from 'presentation/assets/icons/png/3x/icn-abrechnung-fertig@3x-schwarz.png';
import screenIcon from 'presentation/assets/icons/png/3x/icn-bildschirm@3x-schwarz.png';
import houseTransmissionIcon from 'presentation/assets/icons/png/3x/icn-datenuebermittlung@3x-schwarz.png';
import radiatorIcon from 'presentation/assets/icons/png/3x/icn-heizung@3x-schwarz.png';
import discountIcon from 'presentation/assets/icons/png/3x/money-saving@3x.png';
import deviceIcon from 'presentation/assets/icons/png/3x/status-display@3x.png';
import waterdropIcon from 'presentation/assets/icons/png/3x/water-consumption@3x.png';
import calendarIcon from 'presentation/assets/icons/svg/calendar.svg';
import downloadIcon from 'presentation/assets/icons/svg/download-icon.svg';
import { useStyletron } from 'baseui';
import { KIND, SIZE } from 'baseui/button';
import ExternalLink from 'presentation/components/ExternalLink';
import FormControl from 'presentation/components/FormControl';
import Icon, { ICONSIZE } from 'presentation/components/Icon';
import Input from 'presentation/components/Input';
import RedLinkButtonWithArrow from 'presentation/components/RedLinkButtonWithArrow';
import CustomerPortalModal from './components/modal/CustomerPortalModal';
import EEDModal from './components/modal/EEDModal';
import TitleWithPrevPageLink from 'presentation/components/TitleWithPrevPageLink';
import Toggle from 'presentation/components/Toggle';
import Accordion from 'presentation/components/ui/Accordion';
import { WizardStepsContainer } from 'presentation/components/WizardSteps';
import { DISABLE_FLOW_DEEP_LINKING_PROTECTION } from 'config';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getABTestingObject } from 'frameworks_drivers/redux/reducers/abTest/abTesting';
import {
  setDiscountCodeNeedsValidation as setDiscountCodeNeedsValidationInStore,
  setReferralDiscountCode
} from 'frameworks_drivers/redux/reducers/miscellaneous/miscSlice';
import { ProductService } from 'frameworks_drivers/redux/reducers/onboardingInformation';
import {
  addSelectedProductWithName,
  removeSelectedProductWithName,
  setReducedProducts,
  setSelectedProducts
} from 'frameworks_drivers/redux/reducers/onboardingInformation/onboardingInformationSlice';
import { pathOfFirstStepOfFlow, paths } from 'frameworks_drivers/routes';
import store from 'store';
import { styled } from 'styletron-react';
import { translationKeyForCheckboxOption } from 'tools/CheckboxOptionMapper';
import { useMediaQuery } from 'tools/hooks/useMediaQuery';
import { offerTranslationKeyForRadioOption, RadioOptionKeys } from 'tools/RadioOptionMapper';
import { TrackerUtil } from 'tracking/TrackerUtil';
import { DiscountStatusEnum } from '../../../../clients';
import { ContentWrapper } from '../../../components/offerpage/ContentWrapper';
import { toPercentage } from '../../../../utils/number';
import CenteredContent from './components/CenteredContent';
import ContactPartnerInfoBox from './components/ContractPartnerInfoBox';
import EEDInfoBox from './components/EEDInfoBox';
import EntryRow from './components/styled/EntryRow';
import { PriceTag, PriceTagSmall } from './components/styled/PriceTag';
import FullOperatingCostsModal from './components/modal/FullOperatingCostsModal';
import ColdWaterInvoicingModal from './components/modal/ColdWaterInvoicingModal';
import ProviderTransferModal from './components/modal/ProviderTransferModal';
import ErrorModal from './components/modal/ErrorModal';
import ButtonOrder from './components/ButtonOrder';
import { Products, ProductsList } from '../../../../entities/model/Product';
import { useFetchPrices } from '../../../../interface_adapters/hooks/useFetchPrices';
import { PricingRepositoryImpl } from '../../../../interface_adapters/services/PricingRepositoryImpl';
import { PricingDataSourceImpl } from '../../../../interface_adapters/services/PricingAPIDataSource';
import { useApplicationLogger } from '../../../../services/useApplicationLogger';
import { PricingPayloadBuilderImpl } from '../../../../application/ports/PricingPayloadBuilderImpl';
import { useTrackGoogleEvents } from '../../../../interface_adapters/hooks/GoogleTagContext';
import { usePreferredSubmitIntention } from '../../../../interface_adapters/hooks/usePreferredSubmitIntention';
import { environmentService } from '../../../../config/di/container';

const LightGrayBackgroundRow = styled(Row, {
  backgroundColor: TechemTheme.colors.backgroundSecondary
});

const ContentCol = styled(Col, {
  maxWidth: '992px',
  '@media (max-width: 375px)': {
    width: '100%',
    maxWidth: '100%',
    flex: '0 0 100%'
  }
});

const TitleContainer = styled(Container, {
  '@media (max-width: 375px)': {
    paddingLeft: 0,
    paddingRight: 0
  }
});

const generatePercentageString = (decimal?: number) => (decimal ? toPercentage(decimal) : 0);

function formatPrice(t: any, price?: number) {
  if (!price || price <= 0) {
    return t('freePriceText');
  }
  let localPrice = price.toLocaleString('de-DE', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2
  });
  return t('fullPriceText', { priceTerm: localPrice });
}

function formatPriceForAdditionalService(t: any, price?: number) {
  const prefix = !price || price <= 0 ? '' : '+ ';
  const priceText = formatPrice(t, price);
  return prefix + priceText;
}

function formatPriceForBanner(price: string) {
  const priceComponents = price.split(',');
  return (
    <>
      {priceComponents[0]}
      <PriceTagSmall>,{priceComponents[1]}</PriceTagSmall>
    </>
  );
}

let uniqueElementCounter = 0;

function generateUniqueId() {
  return 'id_' + uniqueElementCounter++;
}

function PriceTitle(props: any) {
  const TitleSpan = styled('span', {
    color: '#000000',
    lineHeight: '24px',
    '@media (max-width: 767px)': {
      fontSize: '16px'
    }
  });
  return (
    <Row className="justify-content-center">
      <Col xs={12}>
        <TitleSpan className="fw-bold fs-4">{props.title}</TitleSpan>
      </Col>
    </Row>
  );
}

function RightboundInfoBox(props: any) {
  // Need to create a styled component, otherwise font color won't be picked up!
  const ParagraphElement = styled('p', {
    width: '160px',
    marginLeft: '30px',
    fontSize: '14px',
    lineHeight: '20px',
    color: `${'#007c94'} !important`
  });

  return (
    <div
      className="d-none d-xl-block position-absolute w-auto start-100"
      style={{
        bottom: props.bottomOffset,
        paddingLeft: '20px'
      }}
    >
      <ParagraphElement className="my-2">
        <Trans i18nKey={props.labelTextKey}></Trans>
      </ParagraphElement>
      <img src={arrowWithDottedLine} alt="info-arrow" style={{ width: '87px' }} />
    </div>
  );
}

function MobileInfoBox(props: any) {
  // Need to create a styled component, otherwise font color won't be picked up!
  const ParagraphElementMobile = styled('p', {
    fontSize: '14px',
    lineHeight: '20px',
    color: `${'#007c94'} !important`
  });
  return (
    <Col
      xs={12}
      className="d-block d-xl-none"
      style={{ paddingTop: '20px', backgroundColor: 'white' }}
    >
      <Row className="justify-content-center">
        <Col
          xs={12}
          style={{
            borderTop: `1px solid ${TechemTheme.colors.backgroundSecondary}`
          }}
        >
          <ParagraphElementMobile className="mb-0 text-center pt-3">
            <Trans i18nKey={props.labelTextKey} />
          </ParagraphElementMobile>
        </Col>
      </Row>
    </Col>
  );
}

// TODO: Use a context to hold all relevant data that are needed for the offerpage
// TODO: Split the component into more small components to enable more testing posibilities and also readability
const OfferPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [css] = useStyletron();
  const { trackDisplayOffer } = useTrackGoogleEvents();

  const { setPreferredSubmitIntention } = usePreferredSubmitIntention();

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
    setError
  } = useForm<{ discountCode: string }>({
    defaultValues: {
      discountCode: store.getState().misc.referralDiscountCode
    }
  });
  const discountCode = watch('discountCode');
  let onboardingInfo = store.getState().onboardingInfo;

  let [discountCodeNeedsValidation, setDiscountCodeNeedsValidation] = useState(
    store.getState().misc.discountCodeNeedsValidation
  );

  const [discountSuccessfullyApplied, setDiscountSuccessfullyApplied] = useState(
    !!onboardingInfo.reducedProducts.find((item) => item.name === Products.HeatingCosts)
  );
  const [apiError, setAPIError] = useState<any>(null);

  const sumPrice = () => {
    return onboardingInfo.selectedProducts.reduce((total, product) => total + product.price, 0);
  };

  const logger = useApplicationLogger();

  const pricingRequestDTO = new PricingPayloadBuilderImpl().buildPayload(
    store.getState().onboardingInfo,
    store.getState().misc,
    getABTestingObject()
  );

  const { pricingData, isError, error, isLoading, fetchPrices } = useFetchPrices(
    new PricingRepositoryImpl(new PricingDataSourceImpl()),
    pricingRequestDTO
  );

  useEffect(() => {
    if (!DISABLE_FLOW_DEEP_LINKING_PROTECTION && onboardingInfo.selectedProducts.length === 0) {
      navigate(pathOfFirstStepOfFlow, { replace: true });
    }
  }, [onboardingInfo.selectedProducts, navigate]);

  useEffect(() => {
    if (discountCodeNeedsValidation) {
      store.dispatch(setDiscountCodeNeedsValidationInStore(false));
      setDiscountCodeNeedsValidation(false);

      fetchPrices();
    }
  }, [discountCodeNeedsValidation, setError, fetchPrices]);

  useEffect(() => {
    if (isError) {
      logger.error('Error while fetching prices with discount code applied:', error);
      setAPIError(error);
      return;
    }

    if (!pricingData) return;

    if (pricingData?.discountStatus === DiscountStatusEnum.Applied) {
      store.dispatch(setReducedProducts(pricingData?.products));
      store.dispatch(
        setSelectedProducts(
          store.getState().onboardingInfo.selectedProducts.map((item) => {
            const reducedItem = store
              .getState()
              .onboardingInfo.reducedProducts.find(
                (reducedProduct) => reducedProduct.name === item.name
              );
            return reducedItem
              ? reducedItem
              : store
                  .getState()
                  .onboardingInfo.products.find(
                    (reducedProduct) => reducedProduct.name === item.name
                  );
          })
        )
      );
      setDiscountSuccessfullyApplied(true);
    } else {
      setError('discountCode', { type: pricingData.discountStatus });
    }
  }, [pricingData, setError, isError, error, logger]);

  useEffect(() => {
    const handleScroll = () => {
      // Tends to skip some pixel numbers if scrolling quickly,
      // so let's make sure we save the current offset to make the price banner stickiness appear smoothly.
      const bannerNeedsToBeSticky = window.pageYOffset > calcStickyTopOffset();
      const bannerJustTurnedToSticky = !scrollPositionBelowBanner && bannerNeedsToBeSticky;
      const bannerJustTurnedToNonsticky = scrollPositionBelowBanner && !bannerNeedsToBeSticky;
      if (bannerJustTurnedToSticky || bannerJustTurnedToNonsticky) {
        setBannerTopOffsetBeforeStickinessChanged(
          priceBannerElement.current ? priceBannerElement.current!.getBoundingClientRect().y : 0
        );
        setScrollPositionBelowBanner(bannerNeedsToBeSticky);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  });

  useEffect(() => {
    trackDisplayOffer();
  }, [trackDisplayOffer]);

  const optionInitiallyToggled = (onboardingInfo: any, key: string) => {
    return (
      onboardingInfo.selectedProducts.map((item: ProductService) => item.name).indexOf(key) >= 0
    );
  };

  const [customerPortalModalOpen, setCustomerPortalModalOpen] = useState(false);

  const [fullOperatingCostsToggled, setFullOperatingCostsToggled] = useState(
    optionInitiallyToggled(onboardingInfo, Products.FullOperatingCosts)
  );
  const [fullOperatingCostsModalOpen, setFullOperatingCostsModalOpen] = useState(false);

  const [coldWaterInvoicingToggled, setColdWaterInvoicingToggled] = useState(
    optionInitiallyToggled(onboardingInfo, Products.ColdWaterInvoicing)
  );
  const [coldWaterInvoicingModalOpen, setColdWaterInvoicingModalOpen] = useState(false);

  const [providerChangeServiceToggled, setProviderChangeServiceToggled] = useState(
    optionInitiallyToggled(onboardingInfo, Products.ProviderChangeService)
  );
  const [providerTransferModalOpen, setProviderTransferModalOpen] = useState(false);

  const [eedModalOpen, setEEDModalOpen] = useState(false);

  const [scrollPositionBelowBanner, setScrollPositionBelowBanner] = useState(false);
  const [bannerTopOffsetBeforeStickinessChanged, setBannerTopOffsetBeforeStickinessChanged] =
    useState(0);

  const isMobile = useMediaQuery('(max-width: 576px)');
  const isXXSmall = useMediaQuery('(max-width: 375px)');

  const calcStickyTopOffset = () => {
    let offset = 0;
    // Calculated based on height of header, progress bar placeholder and title view.
    // Respective heights are different for mobile and desktop breakpoints, so we need two different top offsets.
    const header = document.getElementById('header');
    const titleView = document.getElementById('title-view');
    const wizardStepsContainer = document.getElementById('wizard-steps-container');
    if (header) {
      offset += header.offsetHeight;
    }
    if (titleView) {
      offset += titleView.offsetHeight;
    }
    if (wizardStepsContainer) {
      offset += wizardStepsContainer.offsetHeight;
    }
    return offset;
  };

  const priceBannerElement = useRef<HTMLDivElement>(null);

  /**
   * Calculate the price of a product based on a given product key. If the was entered
   * a rabattcode it will be used a reduced price
   *
   * @param {keyof ProductsList} product - The key of the product in the ProductsList.
   * @returns {ProductService} - The calculated price information for the product, or an empty ProductService object
   *     if not found.
   */
  function calculatePrice(product: keyof ProductsList): ProductService | undefined {
    const { reducedProducts, products } = onboardingInfo;

    // Search for a reduced price and return it if exists
    const reducedPrice = getPriceForProduct(product, reducedProducts);
    if (reducedPrice) return reducedPrice;

    return getPriceForProduct(product, products);
  }

  function getPriceForProduct(product: keyof ProductsList, products: ProductService[]) {
    if (!(product in Products)) throw new Error('Product not found in ProductsList');

    return products.find((item) => item.name === Products[product]);
  }

  const saveCurrentProductSelection = () => {
    let selectedProducts: ProductService[] = [];

    const heatingCostPrice = calculatePrice('HeatingCosts');
    const hotWaterInvoicingPrice = calculatePrice('HotWaterInvoicing');

    if (heatingCostPrice) selectedProducts.push(heatingCostPrice);
    if (hotWaterInvoicingPrice) selectedProducts.push(hotWaterInvoicingPrice);

    if (fullOperatingCostsToggled) {
      const fullOperationCostPrice = calculatePrice('FullOperatingCosts');
      if (fullOperationCostPrice) selectedProducts.push(fullOperationCostPrice);
    }

    if (coldWaterInvoicingToggled) {
      const coldWaterInvoicingPrice = calculatePrice('ColdWaterInvoicing');
      if (coldWaterInvoicingPrice) selectedProducts.push(coldWaterInvoicingPrice);
    }
    if (providerChangeServiceToggled) {
      const providerChangeServicePrice = calculatePrice('ProviderChangeService');
      if (providerChangeServicePrice) selectedProducts.push(providerChangeServicePrice);
    }
    store.dispatch(setSelectedProducts(selectedProducts));
  };

  const handleNextButtonClick = () => {
    saveCurrentProductSelection();

    TrackerUtil.trackNextBtnClick();
    navigate(paths.ContactDetails);
  };

  const handlePrevBtnClicked = () => {
    // Saving current product selection even if going back to improve UX.
    saveCurrentProductSelection();

    TrackerUtil.trackPrevBtnClick();
    navigate(paths.InputEMailAddress);
  };

  const buildDiscountCodeErrorMessage = () => {
    switch (errors?.discountCode?.type) {
      case 'required':
        return t('pageOfferDiscountCodeSectionInputErrorEmpty');
      case 'expired':
        return t('pageOfferDiscountCodeSectionInputErrorExpired');
      case 'invalid':
        return t('pageOfferDiscountCodeSectionInputErrorInvalid');
      default:
        return null;
    }
  };

  const loadDiscountedPrices = handleSubmit(({ discountCode }, e) => {
    store.dispatch(setReferralDiscountCode(discountCode));
    store.dispatch(setDiscountCodeNeedsValidationInStore(true));
    setDiscountCodeNeedsValidation(true);

    TrackerUtil.trackNextBtnClick();

    e?.preventDefault();
  });

  const priceBannerContent = (sticky: boolean = false) => {
    const description = () => {
      return (
        <Col xs={12} className="mt-2">
          <span className="copy-s">
            <Trans
              i18nKey="pageOfferTotalSumDescription"
              values={{
                numberOfApartments: onboardingInfo.numberOfApartments,
                centralHeatingSelection: t(
                  offerTranslationKeyForRadioOption(onboardingInfo.centralHeatingAvailable)
                ),
                centralHotWaterSelection: t(
                  offerTranslationKeyForRadioOption(onboardingInfo.centralHotWaterDelivery)
                ),
                heatingTypeSelection: t(
                  offerTranslationKeyForRadioOption(onboardingInfo.heatingType)
                ),
                heatingSourcesSelection: onboardingInfo.heatingSources
                  .map((heatingSourceKey) => {
                    return t(translationKeyForCheckboxOption(heatingSourceKey));
                  })
                  .join(', ')
              }}
            />
          </span>
        </Col>
      );
    };
    return (
      <ContentCol xs={10} lg={9} xl={7} data-gi="price-banner-content">
        <Row>
          <Col xs className="d-flex">
            <Row>
              <Col xs={12} className="mt-auto">
                <PriceTitle title={t('pageOfferTotalSumTitle')} />
              </Col>
              {!isMobile && description()}
            </Row>
          </Col>
          <Col xs="auto">
            <PriceTag className="mx-0 mt-0 fw-semibold" data-gi="banner-price-tag">
              {formatPriceForBanner(formatPrice(t, sumPrice()))}
            </PriceTag>
            <p className="mt-1" style={{ fontSize: '10px' }}>
              {t('pageOfferTotalUstHint')}
            </p>
          </Col>
          {!sticky && isMobile && description()}
        </Row>

        {onboardingInfo.reducedProducts.find((item) => item.name === Products.HeatingCosts) && (
          <Row
            className={`justify-content-between pt-3${sticky ? ' d-none d-md-block' : ''}`}
            style={{ color: '#007c94' }}
            data-gi="discount-code-applied-value"
          >
            <Col xs>
              <Row>
                <Col xs="auto" className="pe-2">
                  <Icon
                    iconSrc={circledCheckmarkIcon}
                    size={ICONSIZE.default}
                    alt="discount success icon"
                  />
                </Col>
                <Col xs className="ps-0">
                  <Row className="justify-content-between">
                    <Col xs={12} md="auto">
                      {t('pageOfferTotalSumDiscountInfoText', {
                        discountPercentage: generatePercentageString(
                          onboardingInfo.selectedProducts.find(
                            (item) => item.name === Products.HeatingCosts
                          )?.appliedDiscount
                        )
                      })}
                    </Col>
                    <Col
                      xs={12}
                      md="auto"
                      className="mt-2 mt-md-0"
                      style={{ textDecoration: 'line-through' }}
                    >
                      {t('pageOfferTotalSumOriginalPriceText', {
                        originalPriceTerm: formatPrice(
                          t,
                          onboardingInfo.selectedProducts
                            .map((item) => item.name)
                            .map(
                              (name) =>
                                onboardingInfo.products.find((item) => item.name === name)!.price
                            )
                            .reduce((prevPrice, currentPrice) => prevPrice + currentPrice)
                        )
                      })}
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
        )}
      </ContentCol>
    );
  };

  const TableEntry = React.memo((props: any) => {
    if (props.hide) return null;
    var valueElements: ReactNode[] = [];

    if (props.values) {
      props.values.forEach((value: string) => {
        valueElements.push(
          <Col xs={12} key={generateUniqueId()}>
            {value}
          </Col>
        );
      });
    }

    var contentCol = props.values ? (
      <Col xs={12} md className="d-flex">
        <Row>{valueElements}</Row>
      </Col>
    ) : (
      ''
    );

    var startEnhancer = props.startElement ? (
      <Col xs="auto" className="pe-0 ps-3" style={{ width: '40px' }}>
        {props.startElement}
      </Col>
    ) : (
      ''
    );
    var endEnhancer = props.endElement ? (
      <Col xs="auto" className="d-flex">
        {props.endElement}
      </Col>
    ) : (
      ''
    );

    return (
      <EntryRow
        className={`position-relative px-3 justify-content-center ${isXXSmall && 'px-0'} ${
          props.additionalClassNames
        }`}
        data-gi={`${props.additionalTestId !== undefined ? ' ' + props.additionalTestId : ''}`}
      >
        {startEnhancer}

        <Col xs>
          <Row>{contentCol}</Row>
        </Col>

        {endEnhancer}

        {props.children}
      </EntryRow>
    );
  });

  const SectionTitle = React.memo((props: any) => {
    return (
      <Row className="justify-content-center">
        <Col xs={12}>
          <h4 className="m-0">{props.title}</h4>
        </Col>
      </Row>
    );
  });

  return (
    <Container fluid>
      <WizardStepsContainer id="wizard-steps-container" />
      <TitleContainer>
        <Row className="justify-content-center">
          <Col id="title-view">
            <TitleWithPrevPageLink
              onPrevClick={() => {
                handlePrevBtnClicked();
              }}
              title={t('pageOfferTitle')}
            />
          </Col>
        </Row>
      </TitleContainer>
      <Row
        className={'py-3 justify-content-center'}
        style={{
          backgroundColor: TechemTheme.colors.backgroundPrimary
        }}
      >
        {priceBannerContent()}
      </Row>
      <Row
        ref={priceBannerElement}
        className="py-3 justify-content-center position-fixed w-100"
        style={{
          zIndex: 1,
          opacity: scrollPositionBelowBanner ? 1 : 0,
          backgroundColor: TechemTheme.colors.backgroundPrimary,
          transition: 'top 0.3s ease-out, box-shadow 0.3s ease-out',
          top: scrollPositionBelowBanner ? bannerTopOffsetBeforeStickinessChanged : '0',
          boxShadow: scrollPositionBelowBanner ? '0 0px 30px 0 rgba(0, 0, 0, 0.2)' : 'none'
        }}
      >
        {priceBannerContent(true)}
      </Row>
      <LightGrayBackgroundRow className="justify-content-center mt-4">
        <ContentCol xs={10} lg={9} xl={7} className="pb-2">
          <Row className="mt-5 mb-4">
            <Col xs={12}>
              <SectionTitle title={t('pageOfferBasicServicesSectionTitle')} />
            </Col>
          </Row>
          <Row>
            <Col xs={12} className="position-relative">
              <TableEntry
                additionalClassNames={`pt-4 ${isXXSmall ? '' : 'rounded-topleft'}`}
                values={[
                  <CenteredContent key={'basic-services-invoicing'}>
                    <ContentWrapper
                      color={TechemTheme.colors.contentPrimary}
                      i18nKey={'pageOfferBasicServicesSectionLabelInvoicingAllRentersTitle'}
                    />

                    <br />

                    <ContentWrapper
                      color={TechemTheme.colors.contentSecondary}
                      i18nKey={'pageOfferBasicServicesSectionLabelInvoicingAllRentersContent'}
                    />

                    {isMobile && <br />}
                    <span className="ms-md-2" style={{ whiteSpace: 'nowrap' }}>
                      <strong
                        data-gi="inline-link sample-invoice"
                        className={css({
                          color: TechemTheme.colors.buttonPrimaryFill,
                          cursor: 'pointer'
                        })}
                      >
                        <ExternalLink
                          name={`${t('pageOfferDummyInvoiceInlineButtonText')}`}
                          href={`${environmentService.getStaticAssetUrl()}/onboarding/musterabrechnung.pdf`}
                          className={css({
                            ':after': {
                              content: `url(${downloadIcon})`,
                              marginLeft: '5px'
                            }
                          })}
                          onClick={() => {
                            TrackerUtil.trackLinkClick(
                              'offerpage_link_dummy-invoice',
                              'link_dummy-invoice'
                            );
                          }}
                        />
                      </strong>
                    </span>
                  </CenteredContent>
                ]}
                startElement={
                  <Icon iconSrc={invoiceIcon} size={ICONSIZE.default} alt="invoice icon" />
                }
                endElement={
                  <Icon iconSrc={circledCheckmarkIcon} size={ICONSIZE.small} alt="checkmark icon" />
                }
              />
              <TableEntry
                values={[
                  <CenteredContent key={'basic-services-contact'}>
                    <ContentWrapper
                      color={TechemTheme.colors.contentPrimary}
                      i18nKey={'pageOfferBasicServicesSectionLabelMeasuringDevicesTitle'}
                    />

                    <br />

                    <ContentWrapper
                      color={TechemTheme.colors.contentSecondary}
                      i18nKey={'pageOfferBasicServicesSectionLabelMeasuringDevicesContent'}
                    />
                  </CenteredContent>
                ]}
                startElement={
                  <Icon iconSrc={deviceIcon} size={ICONSIZE.default} alt="measuring device icon" />
                }
                endElement={
                  <Icon iconSrc={circledCheckmarkIcon} size={ICONSIZE.small} alt="checkmark icon" />
                }
              />
              <TableEntry
                values={[
                  <CenteredContent key={'basic-services-water'}>
                    <ContentWrapper
                      color={TechemTheme.colors.contentPrimary}
                      i18nKey={'pageOfferBasicServicesSectionLabelExactMeteringTitle'}
                    />

                    <br />

                    <ContentWrapper
                      color={TechemTheme.colors.contentSecondary}
                      i18nKey={'pageOfferBasicServicesSectionLabelExactMeteringContent'}
                    />
                  </CenteredContent>
                ]}
                startElement={
                  <Icon iconSrc={radiatorIcon} size={ICONSIZE.default} alt="exact metering icon" />
                }
                endElement={
                  <Icon iconSrc={circledCheckmarkIcon} size={ICONSIZE.small} alt="checkmark icon" />
                }
              />
              <TableEntry
                values={[
                  <CenteredContent key={'wireless-consumption-metering'}>
                    <ContentWrapper
                      color={TechemTheme.colors.contentPrimary}
                      i18nKey={'pageOfferBasicServicesSectionLabelWirelessConsumptionMeteringTitle'}
                    />

                    <br />

                    <ContentWrapper
                      color={TechemTheme.colors.contentSecondary}
                      i18nKey={
                        'pageOfferBasicServicesSectionLabelWirelessConsumptionMeteringContent'
                      }
                    />
                  </CenteredContent>
                ]}
                startElement={
                  <Icon
                    iconSrc={houseTransmissionIcon}
                    size={ICONSIZE.default}
                    alt="wireless consumption metering icon"
                  />
                }
                endElement={
                  <Icon iconSrc={circledCheckmarkIcon} size={ICONSIZE.small} alt="checkmark icon" />
                }
              />
              <TableEntry
                values={[
                  <CenteredContent key={'basic-services-price-guarantee'}>
                    <ContentWrapper
                      color={TechemTheme.colors.contentPrimary}
                      i18nKey={'pageOfferBasicServicesSectionLabelPriceGuaranteeTitle'}
                    />

                    <br />

                    <ContentWrapper
                      color={TechemTheme.colors.contentSecondary}
                      i18nKey={'pageOfferBasicServicesSectionLabelPriceGuaranteeContent'}
                    />
                  </CenteredContent>
                ]}
                startElement={
                  <Icon iconSrc={calendarIcon} size={ICONSIZE.default} alt="price guarantee icon" />
                }
                endElement={
                  <Icon iconSrc={circledCheckmarkIcon} size={ICONSIZE.small} alt="checkmark icon" />
                }
              />
              <TableEntry
                additionalClassNames={`pb-3${isXXSmall ? '' : ' rounded-bottomright'}`}
                values={[
                  <CenteredContent key={'basic-services-customer-portal-access'}>
                    <ContentWrapper
                      color={TechemTheme.colors.contentPrimary}
                      i18nKey={'pageOfferBasicServicesSectionLabelCustomerPortalAccessTitle'}
                    />

                    <br />

                    <ContentWrapper
                      color={TechemTheme.colors.contentSecondary}
                      i18nKey={'pageOfferBasicServicesSectionLabelCustomerPortalAccessContent'}
                    />

                    {isMobile && <br />}
                    <span className="ms-md-2" style={{ whiteSpace: 'nowrap' }}>
                      <RedLinkButtonWithArrow
                        ariaLabel={'Mehr Informationen zum Kundenportal'}
                        handleClick={() => {
                          TrackerUtil.trackLinkClick(
                            'offerpage_link_customer-portal-modal',
                            'link_customer-portal-modal'
                          );
                          setCustomerPortalModalOpen(true);
                        }}
                        additionalTestId="customer-portal"
                      >
                        {t('pageOfferMoreInfoInlineButtonText')}
                      </RedLinkButtonWithArrow>
                    </span>
                  </CenteredContent>
                ]}
                startElement={
                  <Icon iconSrc={screenIcon} size={ICONSIZE.default} alt="customer portal icon" />
                }
                endElement={
                  <Icon iconSrc={circledCheckmarkIcon} size={ICONSIZE.small} alt="checkmark icon" />
                }
              >
                <MobileInfoBox labelTextKey="pageOfferInfoBox1LabelText" />
              </TableEntry>
              <RightboundInfoBox labelTextKey="pageOfferInfoBox1LabelText" bottomOffset="50%" />
            </Col>
          </Row>

          <Row className="mt-5 mb-2">
            <Col xs={12}>
              <SectionTitle title={t('pageOfferAdditionalServicesSectionTitle')} />
            </Col>
          </Row>
          <Row>
            <Col xs={12} className={`my-6`}>
              <EntryRow
                className={`position-relative justify-content-center px-3 py-4 my-3 ${
                  isXXSmall ? '' : 'rounded-topleft'
                }`}
                data-gi="full-operating-costs"
              >
                <Col xs={12} md className={`${isXXSmall ? 'px-0' : ''}`}>
                  <Row>
                    <Col xs="auto" className="pe-0 ps-3" style={{ width: '40px' }}>
                      <Icon
                        iconSrc={invoiceIcon}
                        size={ICONSIZE.default}
                        alt="full operating invoice icon"
                      />
                    </Col>
                    <Col xs>
                      <CenteredContent>
                        <ContentWrapper
                          color={TechemTheme.colors.contentPrimary}
                          i18nKey={'pageOfferAdditionalServicesSectionLabelFullOperatingCosts'}
                        />
                      </CenteredContent>
                    </Col>

                    <Col xs={12} md={11} className="my-3 ms-md-5 ps-md-2">
                      <span className="copy d-inline-block">
                        <ContentWrapper
                          color={TechemTheme.colors.contentPrimary}
                          i18nKey={
                            'pageOfferAdditionalServicesSectionDescriptionFullOperatingCosts'
                          }
                        />

                        <p className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                          <RedLinkButtonWithArrow
                            ariaLabel={'Mehr Informationen zu Betriebskosten'}
                            handleClick={() => {
                              TrackerUtil.trackLinkClick(
                                'offerpage_link_full-operating-costs-modal',
                                'link_full-operating-costs-modal'
                              );
                              setFullOperatingCostsModalOpen(true);
                            }}
                            additionalTestId="full-operating-costs"
                          >
                            {t('pageOfferMoreInfoInlineButtonText')}
                          </RedLinkButtonWithArrow>
                        </p>
                      </span>
                    </Col>
                  </Row>
                </Col>

                <Col xs={12} md="auto" className={`mw-auto${isXXSmall ? ' px-0' : ''}`}>
                  <Row className={`justify-content-between h-100${!isMobile ? ' d-grid' : ''}`}>
                    <Col
                      xs={{ span: 'auto', order: 2 }}
                      md={{ order: 1 }}
                      className="my-auto my-md-0 px-md-3 position-relative"
                    >
                      {onboardingInfo.reducedProducts.find(
                        (item) => item.name === Products.FullOperatingCosts
                      ) && (
                        <Row
                          className="d-block d-md-none position-absolute"
                          style={{ bottom: '32px' }}
                        >
                          <Col xs={12}>
                            <span
                              className="copy"
                              style={{
                                textDecoration: 'line-through',
                                color: '#007c94'
                              }}
                              data-gi="price-original"
                            >
                              {formatPriceForAdditionalService(
                                t,
                                onboardingInfo.products.find(
                                  (item) => item.name === Products.FullOperatingCosts
                                )?.price
                              )}
                            </span>
                          </Col>
                        </Row>
                      )}

                      <Row>
                        <Col xs={12}>
                          <span
                            className="copy text-bold"
                            style={{ color: '#007c94' }}
                            data-gi="price-current"
                          >
                            {formatPriceForAdditionalService(
                              t,
                              onboardingInfo.reducedProducts.find(
                                (item) => item.name === Products.FullOperatingCosts
                              )
                                ? onboardingInfo.reducedProducts.find(
                                    (item) => item.name === Products.FullOperatingCosts
                                  )?.price
                                : onboardingInfo.products.find(
                                    (item) => item.name === Products.FullOperatingCosts
                                  )?.price
                            )}
                          </span>
                        </Col>
                      </Row>

                      {onboardingInfo.reducedProducts.find(
                        (item) => item.name === Products.FullOperatingCosts
                      ) && (
                        <Row className="mt-3 d-none d-md-block">
                          <Col xs={12}>
                            <span
                              className="copy"
                              style={{
                                textDecoration: 'line-through',
                                color: '#007c94'
                              }}
                              data-gi="price-original"
                            >
                              {formatPriceForAdditionalService(
                                t,
                                onboardingInfo.products.find(
                                  (item) => item.name === Products.FullOperatingCosts
                                )?.price
                              )}
                            </span>
                          </Col>
                        </Row>
                      )}
                    </Col>
                    <Col
                      xs={{ span: 'auto', order: 1 }}
                      md={{ order: 2 }}
                      className={`px-2 px-md-3 mt-auto ${isMobile ? 'ms-0' : 'ms-auto'}`}
                    >
                      <Toggle
                        key="0"
                        checked={fullOperatingCostsToggled}
                        onChange={() => {
                          TrackerUtil.trackToggleClick(
                            `${TrackerUtil.getPageName()}_toggle_add-operating-cost-invoice`,
                            'toggle_add-operating-cost-invoice'
                          );
                          store.dispatch(
                            !fullOperatingCostsToggled
                              ? addSelectedProductWithName(Products.FullOperatingCosts)
                              : removeSelectedProductWithName(Products.FullOperatingCosts)
                          );
                          onboardingInfo = store.getState().onboardingInfo;
                          setFullOperatingCostsToggled(!fullOperatingCostsToggled);
                        }}
                      />
                    </Col>
                  </Row>
                </Col>
                <RightboundInfoBox labelTextKey="pageOfferInfoBox2LabelText" bottomOffset="16%" />
                <MobileInfoBox labelTextKey="pageOfferInfoBox2LabelText" />
              </EntryRow>

              <EntryRow
                className={`position-relative justify-content-center px-3 py-4 my-3 ${
                  onboardingInfo.heatingCostsInvoice !==
                    RadioOptionKeys.HeatingCostsInvoice.Professional && !isXXSmall
                    ? 'rounded-bottomright'
                    : ''
                }`}
                data-gi="cold-water-invoicing"
              >
                <Col xs={12} md className={`${isXXSmall ? 'px-0' : ''}`}>
                  <Row>
                    <Col xs="auto" className="pe-0 ps-3" style={{ width: '40px' }}>
                      <Icon
                        iconSrc={waterdropIcon}
                        size={ICONSIZE.default}
                        alt="water consumption icon"
                      />
                    </Col>
                    <Col xs>
                      <CenteredContent>
                        <ContentWrapper
                          color={TechemTheme.colors.contentPrimary}
                          i18nKey={'pageOfferAdditionalServicesSectionLabelWaterConsumption'}
                        />
                      </CenteredContent>
                    </Col>
                    <Col xs={12} md={11} className="my-3 ms-md-5 ps-md-2">
                      <span className="copy d-inline-block">
                        <ContentWrapper
                          color={TechemTheme.colors.contentPrimary}
                          i18nKey={'pageOfferAdditionalServicesSectionDescriptionWaterConsumption'}
                        />

                        <p className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                          <RedLinkButtonWithArrow
                            ariaLabel={'Mehr Informationen zur Kaltwasserabrechnung'}
                            handleClick={() => {
                              TrackerUtil.trackLinkClick(
                                'offerpage_link_cold-water-invoicing-modal',
                                'link_cold-water-invoicing-modal'
                              );
                              setColdWaterInvoicingModalOpen(true);
                            }}
                            additionalTestId="cold-water-invoicing"
                          >
                            {t('pageOfferMoreInfoInlineButtonText')}
                          </RedLinkButtonWithArrow>
                        </p>
                      </span>
                    </Col>
                  </Row>
                </Col>
                <Col xs={12} md="auto" className={`mw-auto${isXXSmall ? ' px-0' : ''}`}>
                  <Row className={`justify-content-between h-100${isMobile ? '' : ' d-grid'}`}>
                    <Col
                      xs={{ span: 'auto', order: 2 }}
                      md={{ order: 1 }}
                      className="my-auto my-md-0 px-md-3 position-relative"
                    >
                      {onboardingInfo.reducedProducts.find(
                        (item) => item.name === Products.ColdWaterInvoicing
                      ) && (
                        <Row
                          className="d-block d-md-none position-absolute"
                          style={{ bottom: '32px' }}
                        >
                          <Col xs={12}>
                            <span
                              className="copy"
                              style={{
                                textDecoration: 'line-through',
                                color: '#007c94'
                              }}
                              data-gi="price-original"
                            >
                              {formatPriceForAdditionalService(
                                t,
                                onboardingInfo.products.find(
                                  (item) => item.name === Products.ColdWaterInvoicing
                                )?.price
                              )}
                            </span>
                          </Col>
                        </Row>
                      )}

                      <Row className="">
                        <Col xs={12}>
                          <span
                            className="copy text-bold"
                            style={{ color: '#007c94' }}
                            data-gi="price-current"
                          >
                            {formatPriceForAdditionalService(
                              t,
                              onboardingInfo.reducedProducts.find(
                                (item) => item.name === Products.ColdWaterInvoicing
                              )
                                ? onboardingInfo.reducedProducts.find(
                                    (item) => item.name === Products.ColdWaterInvoicing
                                  )?.price
                                : onboardingInfo.products.find(
                                    (item) => item.name === Products.ColdWaterInvoicing
                                  )?.price
                            )}
                          </span>
                        </Col>
                      </Row>

                      {onboardingInfo.reducedProducts.find(
                        (item) => item.name === Products.ColdWaterInvoicing
                      ) && (
                        <Row className="mt-3 d-none d-md-block">
                          <Col xs={12}>
                            <span
                              className="copy"
                              style={{
                                textDecoration: 'line-through',
                                color: '#007c94'
                              }}
                              data-gi="price-original"
                            >
                              {formatPriceForAdditionalService(
                                t,
                                onboardingInfo.products.find(
                                  (item) => item.name === Products.ColdWaterInvoicing
                                )?.price
                              )}
                            </span>
                          </Col>
                        </Row>
                      )}
                    </Col>
                    <Col
                      xs={{ span: 'auto', order: 1 }}
                      md={{ order: 2 }}
                      className={`px-2 px-md-3 mt-auto ${isMobile ? 'ms-0' : 'ms-auto'}`}
                    >
                      <Toggle
                        key="1"
                        checked={coldWaterInvoicingToggled}
                        onChange={() => {
                          TrackerUtil.trackToggleClick(
                            `${TrackerUtil.getPageName()}_toggle_add-water-consumption-metering`,
                            'toggle_add-water-consumption-metering'
                          );
                          store.dispatch(
                            !coldWaterInvoicingToggled
                              ? addSelectedProductWithName(Products.ColdWaterInvoicing)
                              : removeSelectedProductWithName(Products.ColdWaterInvoicing)
                          );
                          onboardingInfo = store.getState().onboardingInfo;
                          setColdWaterInvoicingToggled(!coldWaterInvoicingToggled);
                        }}
                      />
                    </Col>
                  </Row>
                </Col>
              </EntryRow>

              <EEDInfoBox setEEDModalOpen={setEEDModalOpen} />

              <ContactPartnerInfoBox />
            </Col>
          </Row>

          <Row className="mt-5 mb-2">
            <Col xs={12}>
              <SectionTitle title={t('pageOfferDiscountCodeSectionTitle')} />
            </Col>
          </Row>
          <Row>
            <Col xs={12} className={`my-6`}>
              <EntryRow
                className={`position-relative justify-content-center px-3 py-2 my-3 ${
                  isXXSmall ? '' : 'rounded-topleft rounded-bottomright'
                }`}
                data-gi="discount-code"
              >
                <Col xs={12} md className={`${isXXSmall ? 'px-0' : ''}`}>
                  <form noValidate onSubmit={loadDiscountedPrices}>
                    <Row>
                      <Col
                        xs="auto"
                        className="pe-0 ps-3 mt-3 mb-auto"
                        style={{ width: '40px', height: '48px' }}
                      >
                        <Icon
                          iconSrc={discountIcon}
                          size={ICONSIZE.default}
                          alt="discount icon"
                          centered
                        />
                      </Col>
                      <Col xs md="auto" className="mt-3 mb-auto d-flex">
                        <div
                          className="mb-auto d-flex"
                          style={{
                            color: TechemTheme.colors.contentPrimary,
                            height: '48px'
                          }}
                        >
                          <span className="my-auto">
                            <Trans i18nKey={'pageOfferDiscountCodeSectionLabelDiscountCode'} />
                          </span>
                        </div>
                      </Col>
                      {discountSuccessfullyApplied ? (
                        <Col
                          xs={12}
                          md
                          className="copy text-bold mt-3 mb-4 py-1 text-end"
                          style={{ color: '#007c94' }}
                        >
                          <Row
                            className="justify-content-end"
                            data-gi="discount-code-applied-value"
                          >
                            <Col xs="auto" className="pe-1">
                              <Icon
                                iconSrc={circledCheckmarkIcon}
                                size={ICONSIZE.default}
                                alt="discount success icon"
                                className="pe-2"
                                centered
                              />
                            </Col>
                            <Col xs="auto" className="ps-0">
                              {t('pageOfferDiscountCodeSectionSuccessText')}
                            </Col>
                          </Row>
                        </Col>
                      ) : (
                        <Col xs={12} lg>
                          <Row>
                            <Col xs={12} sm>
                              <FormControl
                                label={() => (
                                  <label
                                    htmlFor="discount-code-input"
                                    className="form-label visually-hidden"
                                  >
                                    {t('pageOfferDiscountCodeSectionInputPlaceholderText')}
                                  </label>
                                )}
                                error={buildDiscountCodeErrorMessage()}
                                additionalTestId="discount-code"
                              >
                                <Controller
                                  control={control}
                                  rules={{
                                    required: true
                                  }}
                                  render={({ field: { onChange, value } }) => (
                                    <Input
                                      id={'discount-code-input'}
                                      autoComplete={'off'}
                                      size={SIZE.default}
                                      type="text"
                                      error={!!errors.discountCode}
                                      value={value}
                                      onChange={onChange}
                                      placeholder={
                                        t('pageOfferDiscountCodeSectionInputPlaceholderText')!
                                      }
                                      additionalTestId="discount-code"
                                      aria-required="false"
                                      aria-invalid={!!errors.discountCode}
                                      aria-describedby={
                                        errors.discountCode ? 'discount-code-error' : undefined
                                      }
                                    />
                                  )}
                                  name="discountCode"
                                />
                              </FormControl>
                            </Col>
                            <Col xs={12} sm="auto" className="d-flex">
                              <div className="mt-3 mb-3 mb-sm-auto m-auto">
                                <Button
                                  size={SIZE.large}
                                  kind={KIND.secondary}
                                  type="submit"
                                  disabled={!discountCode}
                                  isLoading={isLoading}
                                  additionalTestId="submit-discount-code"
                                >
                                  {t('pageOfferDiscountCodeSectionSubmitButtonText')}
                                </Button>
                              </div>
                            </Col>
                          </Row>
                        </Col>
                      )}
                    </Row>
                  </form>
                </Col>
              </EntryRow>
            </Col>
          </Row>

          <Row className="justify-content-center ">
            <ButtonOrder
              reverseOrder={isMobile}
              onPrimaryClick={() => {
                setPreferredSubmitIntention('order');
                handleNextButtonClick();
              }}
              onSecondaryClick={() => {
                setPreferredSubmitIntention('offer');
                handleNextButtonClick();
              }}
              primaryText={t('pageOfferOrderNowButtonText')}
              secondaryText={t('pageOfferSendViaMailButtonText')}
            />
          </Row>
        </ContentCol>
      </LightGrayBackgroundRow>
      <Row>
        <Col className="mt-5">
          <Col xs className="px-sm-3">
            <div className="py-0 py-md-3" data-gi="page-title-element">
              {isMobile ? (
                <h4 className="m-auto text-center text-break fs-3">
                  <Trans i18nKey={'pageOfferEndSectionTitleMobile'} />
                </h4>
              ) : (
                <h3 className="m-auto text-center text-break">
                  <Trans i18nKey={'pageOfferEndSectionTitle'} />
                </h3>
              )}
            </div>
          </Col>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <ContentCol xs={10} lg={9} xl={7} className="my-5">
          {Array.from({ length: 4 }, (_, i) => i + 1).map((index) => {
            return (
              <>
                <Accordion
                  title={`pageOfferEndSectionFAQ${index}`}
                  content={`pageOfferEndSectionAnswer${index}`}
                  last={index === 4}
                />
              </>
            );
          })}
        </ContentCol>
      </Row>

      <EEDModal isOpen={eedModalOpen} onClose={() => setEEDModalOpen(false)} />

      <CustomerPortalModal
        isOpen={customerPortalModalOpen}
        onClose={() => setCustomerPortalModalOpen(false)}
      />

      <FullOperatingCostsModal
        isOpen={fullOperatingCostsModalOpen}
        onClose={() => setFullOperatingCostsModalOpen(false)}
        onAccept={() => {
          setFullOperatingCostsToggled(true);
          store.dispatch(addSelectedProductWithName(Products.FullOperatingCosts));
          onboardingInfo = store.getState().onboardingInfo;
          setFullOperatingCostsModalOpen(false);
        }}
        onDecline={() => {
          setFullOperatingCostsToggled(false);
          store.dispatch(removeSelectedProductWithName(Products.FullOperatingCosts));
          onboardingInfo = store.getState().onboardingInfo;
          setFullOperatingCostsModalOpen(false);
        }}
        price={formatPrice(
          t,
          onboardingInfo.reducedProducts.find((item) => item.name === Products.FullOperatingCosts)
            ? onboardingInfo.reducedProducts.find(
                (item) => item.name === Products.FullOperatingCosts
              )?.price
            : onboardingInfo.products.find((item) => item.name === Products.FullOperatingCosts)
                ?.price
        )}
      />

      <ColdWaterInvoicingModal
        isOpen={coldWaterInvoicingModalOpen}
        onClose={() => setColdWaterInvoicingModalOpen(false)}
        onAccept={() => {
          setColdWaterInvoicingToggled(true);
          store.dispatch(addSelectedProductWithName(Products.ColdWaterInvoicing));
          onboardingInfo = store.getState().onboardingInfo;
          setColdWaterInvoicingModalOpen(false);
        }}
        onDecline={() => {
          setColdWaterInvoicingToggled(false);
          store.dispatch(removeSelectedProductWithName(Products.ColdWaterInvoicing));
          onboardingInfo = store.getState().onboardingInfo;
          setColdWaterInvoicingModalOpen(false);
        }}
        price={formatPrice(
          t,
          onboardingInfo.reducedProducts.find((item) => item.name === Products.ColdWaterInvoicing)
            ? onboardingInfo.reducedProducts.find(
                (item) => item.name === Products.ColdWaterInvoicing
              )?.price
            : onboardingInfo.products.find((item) => item.name === Products.ColdWaterInvoicing)
                ?.price
        )}
      />

      <ProviderTransferModal
        isOpen={providerTransferModalOpen}
        onClose={() => setProviderTransferModalOpen(false)}
        onAccept={() => {
          setProviderChangeServiceToggled(true);
          store.dispatch(addSelectedProductWithName(Products.ProviderChangeService));
          onboardingInfo = store.getState().onboardingInfo;
          setProviderTransferModalOpen(false);
        }}
        onDecline={() => {
          setProviderChangeServiceToggled(false);
          store.dispatch(removeSelectedProductWithName(Products.ProviderChangeService));
          onboardingInfo = store.getState().onboardingInfo;
          setProviderTransferModalOpen(false);
        }}
      />

      <ErrorModal isOpen={apiError !== null} onClose={() => setAPIError(null)} />
    </Container>
  );
};

export default OfferPage;
